import { date as dateUtils } from '@eti/utils';
import { startOfDay } from 'date-fns';
import type { GetTripReminderInfoQuery, TripType } from '@eti/schema-types';
import {
  ONE_WAY,
  MULTI_STOP,
  OPEN_JAW_SINGLE_ORIGIN,
  OPEN_JAW_SINGLE_DESTINATION,
  OPEN_JAW_DOUBLE,
} from '../constants/tripTypesConstants';
import { getTravelerPrices, getTravelerPricesTotal } from './travelers';

export type TripReminderTripType = NonNullable<GetTripReminderInfoQuery['selectedTrip']>;
type TripReminderBoundType = TripReminderTripType['bounds'][number];
type TripReminderSegmentType = TripReminderBoundType['segments'][number];
type TripReminderTripSegmentType = Extract<TripReminderSegmentType, { __typename?: 'TripSegment' }>;

const multiCityTripTypes: TripType[] = [
  MULTI_STOP,
  OPEN_JAW_SINGLE_ORIGIN,
  OPEN_JAW_SINGLE_DESTINATION,
  OPEN_JAW_DOUBLE,
];
const getFirstBound = (trip: TripReminderTripType) => trip?.bounds[0];

export const getOrigin = (trip: TripReminderTripType) => {
  if (multiCityTripTypes.includes(trip.type)) {
    return trip.bounds.map((bound) => (bound.segments[0] as TripReminderTripSegmentType).origin);
  }

  return (getFirstBound(trip).segments[0] as TripReminderTripSegmentType).origin;
};

export const getDestination = (trip: TripReminderTripType) => {
  if (multiCityTripTypes.includes(trip.type)) {
    return trip.bounds.map(
      (bound) =>
        (bound.segments[bound.segments.length - 1] as TripReminderTripSegmentType).destination,
    );
  }

  const firstBound = getFirstBound(trip);
  return (firstBound.segments[firstBound.segments.length - 1] as TripReminderTripSegmentType)
    .destination;
};

export const getDepartureDate = (trip: TripReminderTripType) => {
  const departureTimestamp = (getFirstBound(trip).segments[0] as TripReminderTripSegmentType)
    .departuredAt;
  return startOfDay(dateUtils.convertToDate(departureTimestamp));
};

export const getReturnDate = (trip: TripReminderTripType) => {
  if (trip.type === ONE_WAY) {
    return null;
  }

  const lastBound = trip.bounds[trip.bounds.length - 1];
  const departureTimestamp = (lastBound.segments[0] as TripReminderTripSegmentType).departuredAt;

  return startOfDay(dateUtils.convertToDate(departureTimestamp));
};

export const getTotalPrice = (trip: TripReminderTripType) => {
  const { travelers, travelerPrices } = trip;
  return getTravelerPricesTotal(getTravelerPrices(travelers, travelerPrices));
};
